import { useCallback, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/hooks/useTranslation';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { deleteDocument, listDocuments } from '@/services/documentVaultService';
import { getFileUrl } from '@/services/fileService';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { DocumentVaultDto } from '@/types/documentVault';
import type { FilterFieldConfig } from '@/types/filters';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { validateTableSearch, buildSortString, toPaginationState } from '@utils/tabHelpers';
import type { TableSearchParams } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import { toDisplayDate } from '@utils/dateHelpers';
import DocumentTypeBadge from '@/features/documents/DocumentTypeBadge';
import RenameDocumentModal from '@/features/documents/RenameDocumentModal';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/documents/')({
  validateSearch: (search: Record<string, unknown>) =>
    validateTableSearch(search, { sortBy: 'updatedOn', sortDir: 'desc' }),
  component: AdminDocumentsPage,
});

function AdminDocumentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const addNotification = useNotificationStore((s) => s.addNotification);

  // MODULE_ALL_DOCUMENTS — single source of truth per backend seed.
  const canCreate = hasPermission('DOCUMENT_REQUESTS_CREATE');
  const canDelete = hasPermission('DOCUMENT_REQUESTS_DELETE');

  const {
    page: urlPage,
    size: urlSize,
    sortBy,
    sortDir,
    filters: filtersParam,
  } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  // ============================================
  // Filters — entityType / documentType / createdOn date-range.
  // Status is pinned to ACTIVE server-side; soft-deleted rows are never surfaced here.
  // ============================================

  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'entityType',
        label: t('K_FILTER_ENTITY_TYPE', 'Entity Type'),
        type: 'select',
        options: [
          { label: t('K_ENTITY_TENANT', 'Client'), value: 'TENANT' },
          { label: t('K_ENTITY_LEGAL_EMPLOYER', 'Legal Entity'), value: 'LEGAL_EMPLOYER' },
          { label: t('K_ENTITY_EMPLOYEE', 'Employee'), value: 'EMPLOYEE' },
        ],
      },
      {
        fieldKey: 'createdOn',
        label: t('K_FILTER_UPLOADED_DATE', 'Uploaded'),
        type: 'date-range',
        fromLabel: t('K_LABEL_FROM', 'From'),
        toLabel: t('K_LABEL_TO', 'To'),
      },
    ],
    [t],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/w/documents',
    filtersParam,
    fields: filterFields,
  });

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // Always pin status=ACTIVE — soft-deleted rows are hidden from the list.
  const mergedPayload = useMemo(() => {
    const baseFilters = (filterPayload?.filters ?? {}) as Record<string, unknown>;
    return { ...filterPayload, filters: { ...baseFilters, status: 'ACTIVE' } };
  }, [filterPayload]);

  // ============================================
  // Columns
  // ============================================

  const columns = useMemo<DataTableColumn<DocumentVaultDto>[]>(
    () => [
      {
        id: 'documentName',
        header: t('K_COLUMN_DOCUMENT_NAME', 'Name'),
        sortable: true,
        sortKey: 'documentName',
        width: '260px',
        cell: (doc) => (
          <span className={styles.cellPrimary} title={doc.documentName}>
            {doc.documentName || '-'}
          </span>
        ),
      },
      {
        id: 'entityType',
        header: t('K_COLUMN_ENTITY_TYPE', 'Entity Type'),
        cell: (doc) => toTitleCase(doc.entityType.replace('_', ' ')),
      },
      {
        id: 'documentType',
        header: t('K_COLUMN_DOCUMENT_TYPE', 'Type'),
        cell: (doc) => <DocumentTypeBadge type={doc.documentType} />,
      },
      {
        id: 'updatedOn',
        header: t('K_COLUMN_UPLOADED', 'Uploaded'),
        sortable: true,
        sortKey: 'updatedOn',
        cell: (doc) => (
          <span className={styles.cellSecondary}>{toDisplayDate(doc.updatedOn)}</span>
        ),
      },
    ],
    [t],
  );

  // ============================================
  // Data
  // ============================================

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['documents', 'all', { page, size, sort: sortString, filterPayload: mergedPayload }],
    queryFn: () => listDocuments({ page, size, sort: sortString }, mergedPayload),
  });

  const rows = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  // ============================================
  // Per-row actions
  // ============================================

  const [renameTarget, setRenameTarget] = useState<DocumentVaultDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocumentVaultDto | null>(null);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['documents'],
      exact: false,
    });
  }, [queryClient]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
    onError: (err: unknown) => {
      const e = err as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
      setDeleteTarget(null);
    },
  });

  const handleDownload = useCallback(
    (doc: DocumentVaultDto) => {
      if (!doc.fileId) {
        addNotification({
          variant: 'critical',
          title: t('K_NOTIF_FAILED_DOWNLOAD_DOCUMENT', 'Cannot download — file reference missing'),
        });
        return;
      }
      window.open(getFileUrl(doc.fileId), '_blank', 'noopener,noreferrer');
    },
    [addNotification, t],
  );

  const rowActions = useCallback(
    (doc: DocumentVaultDto) => {
      const isEnvelope = doc.documentType === 'ENVELOPE';
      const actions: ProfileAction[] = [];
      actions.push({
        id: 'download',
        label: t('K_DOCS_ACTION_DOWNLOAD', 'Download'),
        onClick: () => handleDownload(doc),
      });
      // Write actions: hidden for ENVELOPE rows regardless of permissions (backend
      // /internal/contract-signed handoff owns envelope documents — audit-protected).
      if (!isEnvelope && canCreate) {
        actions.push({
          id: 'rename',
          label: t('K_DOCS_ACTION_RENAME', 'Rename'),
          onClick: () => setRenameTarget(doc),
        });
      }
      if (!isEnvelope && canDelete) {
        actions.push({
          id: 'delete',
          label: t('K_DOCS_ACTION_DELETE', 'Delete'),
          onClick: () => setDeleteTarget(doc),
        });
      }
      return <ProfileActionsMenu actions={actions} />;
    },
    [t, canCreate, canDelete, handleDownload],
  );

  // ============================================
  // Sort / pagination handlers
  // ============================================

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/documents',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/documents',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/documents',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const getRowKey = useCallback((doc: DocumentVaultDto) => doc.id, []);

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <h2 className={styles.emptyTitle}>
            {t('K_EMPTY_NO_DOCUMENTS', 'No documents yet')}
          </h2>
          <p className={styles.emptyText}>
            {t(
              'K_EMPTY_NO_DOCUMENTS_ADMIN',
              'Documents uploaded against tenants, legal entities, or employees will appear here.',
            )}
          </p>
        </div>
      ),
    [listForbidden, t],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_DOCUMENTS', 'Documents')}</h1>
          <p className={styles.subtitle}>
            {t(
              'K_PAGE_SUBTITLE_DOCUMENTS_ADMIN',
              'All documents across tenants. Uploads happen from the entity profile.',
            )}
          </p>
        </div>
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={rows}
        rowKey={getRowKey}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        loadingText={t('K_LOADING_DOCUMENTS', 'Loading documents...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="All documents list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <RenameDocumentModal
        isOpen={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        document={renameTarget}
        onRenamed={invalidate}
      />

      <AlertModal
        variant="warning"
        title={t('K_MODAL_DELETE_DOCUMENT', 'Delete Document')}
        description={t(
          'K_CONFIRM_DELETE_DOCUMENT',
          'Are you sure you want to delete "{name}"? This action can be reversed by an admin.',
        ).replace('{name}', deleteTarget?.documentName ?? '')}
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        primaryLabel={t('K_BUTTON_DELETE', 'Delete')}
        onPrimaryClick={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
